/**
 * The ClassGuardian wiki (#536): every option a parent can use, what it does and how it works, with screenshots from the
 * real app (web/public/wiki, made by tools/shot/wiki.mjs). Parent-facing only: nothing about the Admin portal.
 *
 * Text is plain, with **bold** for names of buttons and places. Each article has a stable id, so any page can link to
 * /wiki#<id>.
 */

export type WikiCategory =
  | 'start'
  | 'home'
  | 'live'
  | 'learning'
  | 'child'
  | 'reports'
  | 'family'
  | 'settings'
  | 'student'
  | 'help';

export const WIKI_CATEGORIES: { id: WikiCategory; label: string }[] = [
  { id: 'start', label: 'Getting started' },
  { id: 'home', label: 'Home & daily use' },
  { id: 'live', label: 'Live help' },
  { id: 'learning', label: 'Learning Hub' },
  { id: 'child', label: 'Each child' },
  { id: 'reports', label: 'Reports' },
  { id: 'family', label: 'Family & sharing' },
  { id: 'settings', label: 'Your settings' },
  { id: 'student', label: 'Your child’s side' },
  { id: 'help', label: 'Help & support' },
];

/** A paragraph, a bulleted list, or numbered steps. */
export type WikiBlock = { p: string } | { list: string[] } | { steps: string[] };

export interface WikiArticle {
  id: string;
  title: string;
  category: WikiCategory;
  /** One line under the title, and what search results show. */
  summary: string;
  /** Where to find it in the app. */
  where?: string;
  body: WikiBlock[];
  shots?: { src: string; caption: string }[];
  /** Extra words people might search for. */
  keywords?: string[];
  related?: string[];
}

const shot = (name: string, caption: string) => ({ src: `/wiki/${name}.jpg`, caption });

export const WIKI_ARTICLES: WikiArticle[] = [
  // --- Getting started -------------------------------------------------------------------------------------------
  {
    id: 'welcome',
    title: 'What ClassGuardian does',
    category: 'start',
    summary: 'A homeschool companion: keep school time focused and safe, and keep the schoolwork organized.',
    body: [
      { p: 'ClassGuardian has two halves that work together:' },
      {
        list: [
          '**During school time** it helps your child stay on task: only the sites and apps you allow, a way to ask you for help or a break, and live view so you can see their screen and talk to them.',
          '**All the time** it keeps the schoolwork organized: a planner, lesson plans, a journal, a grade book with report cards, attendance and a reading log.',
        ],
      },
      { p: 'A banner shows on your child’s computer whenever you watch or talk, unless you turn on Hidden mode, or Ghost mode for the screen (the camera and microphone always show it). Live view is never recorded.' },
      { p: 'To start, add a child, pair their computer and set their school hours.' },
    ],
    keywords: ['overview', 'about', 'introduction'],
    related: ['add-child', 'set-up-computer', 'school-time'],
  },
  {
    id: 'add-child',
    title: 'Add a child',
    category: 'start',
    summary: 'Each child gets their own page, schedule, safety controls and schoolwork.',
    where: '**Home → Add child**',
    body: [
      { steps: ['On **Home**, choose **Add child**.', 'Type their name (a nickname is fine) and pick a color.', 'Open the child to set up their computer, school hours and subjects.'] },
      { p: 'New children start on your family school schedule (if you set one), with your family’s feature choices, and with “Only allow learning sites during school time” turned on.' },
      { p: 'Your plan decides how many children you can add. Remove a child from their **Profile** tab; that deletes their schedule, rules, activity and devices.' },
    ],
    shots: [shot('home', 'Home with two children')],
    keywords: ['new child', 'student', 'kid'],
    related: ['set-up-computer', 'child-profile', 'school-settings'],
  },
  {
    id: 'set-up-computer',
    title: 'Set up your child’s computer',
    category: 'start',
    summary: 'Pair the computer once with a 6-digit Family code; ClassGuardian then runs on its own during school time.',
    where: 'Open your child → **Student → Devices → Pair a new computer**',
    body: [
      { steps: [
        'On your child’s **Devices** tab choose **Pair a new computer**. Download the **Windows** or **Mac** app there, or note the browser address for a Chromebook.',
        'Choose **Get a Family code**. It works for 15 minutes.',
        'On your child’s computer, install and open the app (or open the browser page) and type the Family code. Sit with your child so you can explain what ClassGuardian shows them.',
        'Add the ClassGuardian browser extension to their browser too, for website blocking and the website log.',
      ] },
      { p: 'Setting it up from your own computer? **Copy the setup instructions** puts the steps and the code on your clipboard to send to their device.' },
      { p: 'Once paired, the computer shows up under **Paired devices**, and School Resources appears in the corner of your child’s screen during school time.' },
    ],
    shots: [shot('child-devices-pair', 'Pair a new computer, with a Family code'), { src: '/help/agent-code.jpg', caption: 'On your child’s computer: enter the Family code' }],
    keywords: ['pair', 'install', 'family code', 'pairing code', 'windows', 'mac', 'chromebook', 'agent'],
    related: ['devices', 'browser-extension', 'school-resources'],
  },
  {
    id: 'school-time',
    title: 'How school time works',
    category: 'start',
    summary: 'Monitoring, website rules and live view only happen during school time.',
    body: [
      { p: '**School time** comes from each child’s weekly schedule. During it, ClassGuardian enforces their safety controls, logs the websites they visit, lets you use live view, and shows School Resources on their computer. Outside it, nothing is monitored.' },
      { list: [
        '**Follow schedule** (the usual setting) switches school time on and off by the schedule.',
        '**School now** starts school time right away, for example on a Saturday catch-up.',
        '**No school** turns it off, for example on a sick day.',
        'A **break** pauses everything for a few minutes, then school time carries on by itself.',
        '**Holidays and breaks** on your school calendar, and a child’s yearly pause, count as no school.',
      ] },
    ],
    shots: [shot('child-schedule', 'A child’s weekly school hours')],
    keywords: ['school hours', 'monitoring', 'schedule', 'free time'],
    related: ['mode-switch', 'child-schedule', 'school-settings', 'breaks'],
  },
  {
    id: 'install-app',
    title: 'Install ClassGuardian on your phone or computer',
    category: 'start',
    summary: 'Add ClassGuardian to your home screen so it opens like an app and can send notifications.',
    where: '**Settings → Notifications**',
    body: [
      { p: 'ClassGuardian works in any modern browser. Installing it gives you an app icon and lets it notify you even when it’s closed.' },
      { list: ['**iPhone or iPad:** open ClassGuardian in Safari, tap **Share**, then **Add to Home Screen**.', '**Android, Chrome or Edge:** use the browser menu and choose **Install ClassGuardian** (or **Add to Home Screen**).'] },
      { p: 'Then turn on **Allow on-device notifications** in Settings → Notifications.' },
    ],
    keywords: ['pwa', 'home screen', 'phone app', 'iphone', 'android'],
    related: ['notifications'],
  },

  // --- Home & daily use ------------------------------------------------------------------------------------------
  {
    id: 'home',
    title: 'Home',
    category: 'home',
    summary: 'Your family at a glance: who’s in school, what needs you, and what’s happening.',
    where: '**Home**',
    body: [
      { list: [
        '**Summary tiles:** children in school now, devices online, assignments due today, overdue assignments, and unread messages. Tap one for the details.',
        '**A card for each child** with their status (school time, a break, free time), their devices, and a quick **Break** button.',
        '**To-do list** for the grown-ups, shared with everyone in your family.',
        '**Recent activity** across your children.',
      ] },
      { p: 'Tiles for features you’ve turned off disappear, for example the assignment tiles if no child uses the planner.' },
    ],
    shots: [shot('home', 'Home')],
    keywords: ['dashboard', 'tiles', 'overview'],
    related: ['todo', 'breaks', 'done-for-today'],
  },
  {
    id: 'todo',
    title: 'To-do list',
    category: 'home',
    summary: 'A shared list for the grown-ups: books to order, trips to book.',
    where: '**Home → To-do list**',
    body: [
      { p: 'Add a task with an optional due date; tick it off when it’s done. Everyone in your family shares the list and sees who finished what. The last 30 finished tasks stay underneath.' },
    ],
    keywords: ['tasks', 'reminders'],
    related: ['home'],
  },
  {
    id: 'mode-switch',
    title: 'Follow schedule, School now and No school',
    category: 'home',
    summary: 'Override the schedule for one child, any time.',
    where: 'Open your child → the switch under their name',
    body: [
      { list: [
        '**Follow schedule:** school time follows the weekly hours, holidays and pauses.',
        '**School now:** school time starts immediately, whatever the schedule says.',
        '**No school:** school time is off until you switch back.',
      ] },
      { p: 'Your child sees the change on their computer straight away.' },
    ],
    keywords: ['override', 'start school', 'stop school', 'sick day'],
    related: ['school-time', 'done-for-today'],
  },
  {
    id: 'breaks',
    title: 'Breaks',
    category: 'home',
    summary: 'Give your child a short break.',
    where: '**Break** on Home or on your child’s page',
    body: [
      { p: 'Choose **Break** and a length. During a break monitoring pauses: no live view and no website log. School time carries on by itself afterwards.' },
    ],
    keywords: ['pause', 'rest'],
    related: ['school-time', 'notifications'],
  },
  {
    id: 'done-for-today',
    title: '“I’m done for today”',
    category: 'home',
    summary: 'When your child finishes early, they can tell you, and you can end school time for the day.',
    where: 'A banner on **Home**, or **Done for today** on your child’s page',
    body: [
      { p: 'Your child presses **I’m done for today** on their computer or in the Student Portal. You get a notification and a banner on Home. **End school for today** turns school time off for the rest of the day; the normal schedule starts again tomorrow. **Not yet** tells them to keep going.' },
      { p: 'You can also end the day yourself with **Done for today** on your child’s page.' },
    ],
    keywords: ['finished early', 'end school'],
    related: ['mode-switch', 'school-resources'],
  },

  // --- Live help -------------------------------------------------------------------------------------------------
  {
    id: 'live-view',
    title: 'Live view',
    category: 'live',
    summary: 'See your child’s screen and camera, hear them, and talk to them during school time.',
    where: 'Open your child → **Collaboration → Live view**',
    body: [
      { p: 'Live view connects to your child’s computer while it’s school time and they’re not on a break. You see their screen (and webcam if it’s on) and hear their microphone and computer sound.' },
      { list: [
        '**Hold to talk** speaks to your child while you hold it; **Open mic** keeps talking on.',
        '**My camera** shows you to your child.',
        '**Pointer** draws a pointer on their screen to show them where to click.',
        '**Full screen** makes the view bigger.',
      ] },
      { p: 'Your child sees a banner while you watch (unless you turn on Hidden mode), and live view is never recorded.' },
    ],
    shots: [shot('live-view', 'Live view, on a phone'), shot('agent-watching', 'What your child sees while you watch')],
    keywords: ['watch', 'screen', 'camera', 'webcam', 'talk', 'microphone', 'pointer'],
    related: ['interact', 'whiteboard', 'chat'],
  },
  {
    id: 'interact',
    title: 'Interact (control the computer)',
    category: 'live',
    summary: 'Take over the mouse and keyboard from live view to show your child something.',
    where: 'Live view → **Interact**',
    body: [
      { p: 'While watching, turn on **Interact** to move the mouse and type on your child’s computer. Your child is told you’re controlling their computer. It needs the Windows or Mac app, and it’s a feature you can hide per child.' },
    ],
    keywords: ['remote control', 'mouse', 'keyboard', 'take over'],
    related: ['live-view', 'child-features'],
  },
  {
    id: 'whiteboard',
    title: 'Whiteboard',
    category: 'live',
    summary: 'A shared board you and your child both draw on, live.',
    where: 'Open your child → **Collaboration → Whiteboard**',
    body: [
      { p: 'Draw with colors and an eraser; **Clear** wipes it. It opens on your child’s computer when their app is online, and they can draw too. Handy for explaining a math problem.' },
    ],
    shots: [shot('child-whiteboard', 'The whiteboard')],
    keywords: ['draw', 'sketch'],
    related: ['live-view', 'chat'],
  },
  {
    id: 'chat',
    title: 'Chat',
    category: 'live',
    summary: 'Send your child messages, and read theirs.',
    where: 'Open your child → **Collaboration → Chat**',
    body: [
      { p: 'Messages reach School Resources on your child’s computer and the Student Portal. Your child can write back from either. If they’re offline they’ll see it next time. New messages show on Home as unread messages, and can notify you.' },
    ],
    shots: [shot('child-chat', 'Chat with your child')],
    keywords: ['message', 'text'],
    related: ['notifications', 'school-resources'],
  },

  // --- Learning Hub ------------------------------------------------------------------------------------------------
  {
    id: 'planner',
    title: 'Planner',
    category: 'learning',
    summary: 'Assignments and due dates on a month, week or day calendar.',
    where: 'Open your child → **Learning Hub → Planner**',
    body: [
      { list: [
        '**New assignment:** a title, subject, due date and optional start and end times. Mark it **graded** to put it in the grade book.',
        '**Repeat** an assignment daily (school days) or weekly, until a date. Deleting one asks whether to delete just it or the rest of the series.',
        'Your child sees the planner in the Student Portal and School Resources, ticks work off and **turns it in** with a note and a sticker.',
        '**Month, Week and Day** views; overdue work also shows on Home.',
      ] },
      { p: 'Lesson plans and journal entries appear on the same calendar.' },
    ],
    shots: [shot('child-planner', 'The planner, month view')],
    keywords: ['assignments', 'calendar', 'homework', 'due date', 'turn in', 'recurring'],
    related: ['link-planner', 'lesson-plans', 'journal', 'grade-book'],
  },
  {
    id: 'link-planner',
    title: 'Link an online school planner',
    category: 'learning',
    summary: 'Bring assignments in from an online school’s calendar feed.',
    where: 'Planner → **Link a planner**',
    body: [
      { p: 'If your child’s online school (for example Connexus / Pearson Online Classroom) gives you a calendar feed address, paste it here. ClassGuardian imports the lessons into the planner and keeps them up to date in the background.' },
    ],
    keywords: ['connexus', 'pearson', 'ical', 'calendar feed', 'import'],
    related: ['planner'],
  },
  {
    id: 'lesson-plans',
    title: 'Lesson plans',
    category: 'learning',
    summary: 'Plan a lesson: focus, objectives, materials, activity and assessment.',
    where: 'Planner → a day → **Lesson plan**',
    body: [
      { p: 'A lesson plan has a subject and topic, the lesson focus and goals, learning objectives, a materials checklist you tick as you gather them, the structure or activity, and how you’ll assess it. Plans show on the planner calendar.' },
    ],
    keywords: ['lesson', 'teaching plan', 'objectives', 'materials'],
    related: ['planner', 'journal'],
  },
  {
    id: 'journal',
    title: 'Homeschool journal',
    category: 'learning',
    summary: 'A note for each day: what you covered and how it went.',
    where: 'Planner → a day → **Journal**',
    body: [{ p: 'Write a short entry for any day. Entries show on the planner calendar and in your exported data, and make a handy record for portfolios.' }],
    keywords: ['diary', 'notes', 'portfolio'],
    related: ['planner', 'lesson-plans'],
  },
  {
    id: 'grade-book',
    title: 'Grade book',
    category: 'learning',
    summary: 'Scores by subject, averaged into a percentage and a letter.',
    where: 'Open your child → **Learning Hub → Grade Book**',
    body: [
      { list: [
        '**Add assignment** with points earned and possible, a weight, a date and a category. Graded planner items appear here to score.',
        'Each subject shows its average; the top shows the overall grade.',
        'Grades read from **Connexus** are averaged the way Connexus does: by the course’s weight for each assessment type (Tests, Quizzes, Portfolios…), and the overall is the average of the courses.',
        '**Grading scale** sets the lowest percentage for each letter for your family.',
        '**Download report** makes a report card or progress report.',
      ] },
    ],
    shots: [shot('child-grades', 'The grade book')],
    keywords: ['grades', 'scores', 'marks', 'gpa', 'letter grade', 'categories', 'weights'],
    related: ['report-cards', 'planner'],
  },
  {
    id: 'report-cards',
    title: 'Report cards and progress reports',
    category: 'learning',
    summary: 'Download a report card or progress report for a period.',
    where: 'Grade Book → **Download report**; periods in **Settings → School Settings**',
    body: [
      { p: 'Report cards follow your report periods: quarters, semesters or your own dates, set in School Settings for the whole family. Each period becomes available once it has ended. A progress report covers any range you choose.' },
    ],
    keywords: ['report card', 'progress report', 'quarters', 'semesters', 'terms'],
    related: ['grade-book', 'school-settings'],
  },
  {
    id: 'attendance',
    title: 'Attendance',
    category: 'learning',
    summary: 'Record the hours your child actually did school each day.',
    where: 'Open your child → **Learning Hub → Attendance**',
    body: [
      { p: 'Type each day’s hours (and a reason for a short or missed day). Next to it ClassGuardian shows its **system estimate**: active time on a monitored computer during school time. It’s a handy check, but your recorded hours are the official ones.' },
      { p: 'Holidays and breaks on your school calendar show as no school. Download attendance reports from **Reports**.' },
    ],
    shots: [shot('child-attendance', 'A week of attendance')],
    keywords: ['hours', 'present', 'absent', 'days'],
    related: ['reports', 'school-settings'],
  },
  {
    id: 'reading-log',
    title: 'Reading log',
    category: 'learning',
    summary: 'Books, pages and minutes, signed off by you.',
    where: 'Open your child → **Learning Hub → Reading Log**',
    body: [
      { p: 'Your child adds what they read in the Student Portal; you can add entries too (yours are signed off already). Tick **Signed off** once you’ve checked one; after that your child can’t change it. The total pages and minutes are at the bottom.' },
    ],
    shots: [shot('child-reading', 'The reading log')],
    keywords: ['books', 'reading', 'sign off', 'pages', 'minutes'],
    related: ['student-portal'],
  },

  // --- Each child --------------------------------------------------------------------------------------------------
  {
    id: 'online-activity',
    title: 'Online Activity',
    category: 'child',
    summary: 'What happened during school time: websites, alerts, messages and more.',
    where: 'Open your child → **Student → Online Activity**',
    body: [
      { p: 'A timeline of the websites your child visited, blocked attempts, focus alerts, breaks, turned-in work, reading and messages. Filter by **Everything**, **Alerts**, **Websites** or **Messages**.' },
      { p: 'Only school time is recorded. Website history is deleted automatically after the server’s retention period (90 days by default).' },
    ],
    shots: [shot('child-activity', 'Online Activity')],
    keywords: ['history', 'websites', 'log', 'alerts', 'timeline'],
    related: ['safety-controls', 'reports'],
  },
  {
    id: 'safety-controls',
    title: 'Safety Controls',
    category: 'child',
    summary: 'Which websites and apps your child can use during school time.',
    where: 'Open your child → **Student → Safety Controls**',
    body: [
      { list: [
        '**Learning sites** are the sites your child needs for school.',
        '**Only allow learning sites during school time** blocks everything else with a friendly page, and alerts you. Turn it off to block only the sites you list instead.',
        '**Block during school time:** whole categories (games, chat and messaging, social media, video and streaming, music). They’re blocked as websites by the extension and as apps on Windows or Mac.',
        '**Apps on your child’s computers:** as they use apps, they appear here so you can block any one, or **approve** it so it’s never closed and counts toward the attendance estimate.',
      ] },
    ],
    shots: [shot('child-sites', 'Safety Controls')],
    keywords: ['block', 'allow', 'websites', 'apps', 'games', 'youtube', 'social media', 'filter', 'approved apps', 'learning apps'],
    related: ['browser-extension', 'online-activity', 'child-features'],
  },
  {
    id: 'child-schedule',
    title: 'A child’s school hours',
    category: 'child',
    summary: 'The weekly hours when school time is on, plus a yearly pause.',
    where: 'Open your child → **Student → Schedule**',
    body: [
      { list: [
        'Set start and end times for each day; **Add time** gives a day more than one block, and **Use for all weekdays** copies a day across.',
        'Days with no times are **No school**.',
        '**Pause school time every year** turns school time off between two dates (for summer), unless you start it by hand.',
      ] },
      { p: 'To set everyone’s hours at once, use the family school schedule in Settings → School Settings.' },
    ],
    shots: [shot('child-schedule', 'A child’s schedule')],
    keywords: ['hours', 'timetable', 'summer', 'pause'],
    related: ['school-time', 'school-settings'],
  },
  {
    id: 'child-features',
    title: 'Features for one child',
    category: 'child',
    summary: 'Hide what one child doesn’t use; nothing is deleted.',
    where: 'Open your child → **Student → Features**',
    body: [
      { p: 'Turn off any feature (live view, interact, whiteboard, chat, planner, lesson plans, journal, grade book, report cards, attendance, reading log, the Student Portal, their schedule view, help and done requests, app blocking). It disappears from your pages, the Student Portal and School Resources for that child. Turn it back on any time and everything is still there.' },
      { p: '**Use these for all my children** copies this child’s choices to the others.' },
    ],
    shots: [shot('child-features', 'Features for one child')],
    keywords: ['hide', 'turn off', 'disable', 'feature flags'],
    related: ['family-features'],
  },
  {
    id: 'devices',
    title: 'Devices',
    category: 'child',
    summary: 'Your child’s paired computers: status, version and updates.',
    where: 'Open your child → **Student → Devices**',
    body: [
      { list: [
        'Each paired computer shows its type (app or browser extension), platform, whether it’s online, and its app version.',
        'The Windows and Mac apps update themselves; you can see an update’s progress here.',
        'Mark a computer the family **shares** so each child signs in on it (see Shared computers).',
        '**Unpair** (the trash icon) a computer you no longer use.',
      ] },
    ],
    shots: [shot('child-devices', 'Paired devices')],
    keywords: ['computer', 'unpair', 'unlink', 'remove', 'update', 'version', 'paired'],
    related: ['set-up-computer', 'shared-computers', 'browser-extension'],
  },
  {
    id: 'child-profile',
    title: 'Profile, subjects and the Student Portal',
    category: 'child',
    summary: 'Name, age, grade and color; subjects; turning in work; their own sign-in.',
    where: 'Open your child → **Student → Profile**',
    body: [
      { list: [
        '**About:** name or nickname, age, grade and color. **Remove** deletes the child and their data.',
        '**Subjects:** pick the subjects they study (or add your own); they fill the subject lists in the planner, lesson plans and grade book.',
        '**Require “What I learned today”:** your child writes what they learned before turning work in.',
        '**Student Portal:** turn on their own sign-in at /student. You get a sign-in code and password, and can turn access off any time.',
      ] },
    ],
    shots: [shot('child-profile', 'A child’s profile')],
    keywords: ['name', 'grade', 'age', 'subjects', 'password', 'student login'],
    related: ['student-portal', 'planner'],
  },

  // --- Reports -----------------------------------------------------------------------------------------------------
  {
    id: 'reports',
    title: 'Reports',
    category: 'reports',
    summary: 'Activity and attendance for any date range, to view or download.',
    where: '**Reports**',
    body: [
      { list: [
        'Pick a child and a range: 7, 30 or 90 days, the school year, or custom dates.',
        'See active time on learning sites, visits, blocked attempts, off-task alerts and days active, plus top sites and a day-by-day table.',
        '**System Estimated Attendance** shows which school days your child was active (an estimate, not official attendance).',
        '**Download:** the attendance report (hours you recorded), the activity report, the estimated attendance report, time per learning site, or a CSV.',
      ] },
    ],
    shots: [shot('reports', 'Reports')],
    keywords: ['report', 'export', 'csv', 'statistics', 'download'],
    related: ['attendance', 'online-activity'],
  },

  // --- Family & sharing ----------------------------------------------------------------------------------------------
  {
    id: 'family-sharing',
    title: 'Share your family with another adult',
    category: 'family',
    summary: 'Invite a co-parent, tutor or relative, and choose what they see and do.',
    where: '**Settings → Family and Sharing**',
    body: [
      { steps: [
        'Type their email, choose what they **can do** (Co-parent, Tutor, Viewer or a role your server adds), and choose **Invite**.',
        'They get an email. New to ClassGuardian? **Create account and join** puts them straight into your family; they don’t need a family of their own.',
        'Under their name, **Can see** chooses which children they see, and **Can do** changes their role or sets Custom levels.',
      ] },
      { p: 'Everyone in the family shares the children you let them see, the to-do list and the family’s settings their role allows.' },
    ],
    shots: [shot('settings-family', 'Family and Sharing')],
    keywords: ['invite', 'co-parent', 'spouse', 'tutor', 'grandparent', 'share'],
    related: ['roles', 'make-owner', 'join-invite'],
  },
  {
    id: 'roles',
    title: 'What each role can do',
    category: 'family',
    summary: 'Co-parent, Tutor, Viewer, or Custom levels for each area.',
    where: '**Settings → Family and Sharing → Can do**',
    body: [
      { list: [
        '**Co-parent:** everything, like you.',
        '**Tutor:** schoolwork and chat; no live view, online activity or the child’s settings.',
        '**Viewer:** sees everything, changes nothing.',
        '**Custom:** choose a level for each area.',
      ] },
      { p: 'The levels are **None** (hidden), **View**, **Edit** (add and change) and **Full** (also delete). The areas are live view and remote help, chat, whiteboard, planner and calendar, lesson plans, journal, grade book and report cards, attendance, reading log, online activity and reports, school time (breaks, done for today), and the child’s settings.' },
      { p: 'Roles also decide whether someone can change family settings like School Settings. What a role can’t see also stays out of their notifications, API keys and data export. The owner always has full access.' },
    ],
    keywords: ['permissions', 'access', 'levels', 'custom', 'can do'],
    related: ['family-sharing'],
  },
  {
    id: 'make-owner',
    title: 'Hand your family to another adult',
    category: 'family',
    summary: 'Make someone else the family’s owner; you stay as a co-parent.',
    where: '**Settings → Family and Sharing → Make owner**',
    body: [{ p: 'The owner invites and removes people, sets roles and manages the plan. **Make owner** on another adult hands that over; you stay in the family as a Co-parent with every child.' }],
    keywords: ['owner', 'transfer', 'ownership'],
    related: ['family-sharing', 'delete-account'],
  },
  {
    id: 'rename-family',
    title: 'Rename your family',
    category: 'family',
    summary: 'Change the family name everyone sees.',
    where: '**Settings → Family and Sharing → Rename** (owner)',
    body: [{ p: 'The name shows in Family and Sharing, invites and emails. Press Enter to save or Esc to cancel.' }],
    keywords: ['family name'],
    related: ['family-sharing'],
  },
  {
    id: 'combine-families',
    title: 'Combine two families into one',
    category: 'family',
    summary: 'One household, one family and one plan.',
    where: '**Settings → Family and Sharing → Combine with another family**',
    body: [
      { steps: [
        'The owner of the family that stays enters the other family owner’s email.',
        'That owner gets an email and sees the request in Family and Sharing, with a side-by-side of every setting: name, school year, schedule, holidays (or keep both), report periods, grading scale and features.',
        'They pick what to keep and choose **Combine families**.',
      ] },
      { p: 'The children (with their devices, history, grades and planner) and adults move over, to-dos combine, the other family is removed and the surviving family keeps its plan. It can’t be undone.' },
    ],
    keywords: ['merge', 'join families', 'combine'],
    related: ['family-sharing', 'more-than-one-family'],
  },
  {
    id: 'more-than-one-family',
    title: 'Belonging to more than one family',
    category: 'family',
    summary: 'Switch between families; start your own if you need one.',
    where: '**Settings → Family**',
    body: [
      { p: 'If you help in more than one family (say, a tutor or grandparent), a **Family** switcher appears at the top of Settings. Family settings, the to-do list and new children go to the family you choose.' },
      { p: 'Joined someone’s family and have children of your own to add? **Start your own family** in Family and Sharing.' },
    ],
    keywords: ['switch family', 'start a family', 'active family'],
    related: ['family-sharing', 'join-invite'],
  },
  {
    id: 'join-invite',
    title: 'Accepting an invite',
    category: 'family',
    summary: 'Join a family from the email link, with or without an account.',
    body: [{ p: 'Open the link in the invite email. If you already have an account, sign in and choose **Join**. If not, choose **Create account and join**: you join that family directly with the role you were invited as, and don’t get an empty family of your own.' }],
    keywords: ['invitation', 'join', 'sign up'],
    related: ['family-sharing', 'more-than-one-family'],
  },

  // --- Your settings -------------------------------------------------------------------------------------------------
  {
    id: 'family-features',
    title: 'Features for all your children',
    category: 'settings',
    summary: 'Turn features on or off for every child at once.',
    where: '**Settings → Features for your children**',
    body: [{ p: 'The same switches as a child’s Features tab, applied to all of them; children you add later start this way too. Each child’s page can still change it for just that child. Nothing is deleted.' }],
    shots: [shot('settings-features', 'Features for your children')],
    keywords: ['hide', 'turn off'],
    related: ['child-features'],
  },
  {
    id: 'school-settings',
    title: 'School Settings',
    category: 'settings',
    summary: 'Family school hours, school year, holidays and report periods.',
    where: '**Settings → School Settings**',
    body: [
      { list: [
        '**Family school schedule:** set the hours once and **push** them to the children you pick. Each child keeps their own copy you can still change.',
        '**School calendar:** the first and last day of the school year, and holidays and breaks. Tap the US holidays your family observes to put them on the calendar (or **All**/**None**), and add your own breaks; past ones hide under **Show past**. These days are no school for the whole family and count as vacation in reports.',
        '**Report periods:** quarters, semesters or custom dates for report cards.',
      ] },
    ],
    shots: [shot('settings-school', 'School Settings')],
    keywords: ['holidays', 'school year', 'calendar', 'vacation', 'quarters', 'semesters', 'family schedule'],
    related: ['school-time', 'child-schedule', 'report-cards'],
  },
  {
    id: 'your-plan',
    title: 'Your plan',
    category: 'settings',
    summary: 'What your subscription includes.',
    where: '**Settings → Your plan**',
    body: [{ p: 'Shows your plan, how many adults and children it includes, and any add-ons. Features your plan doesn’t include show a short note where you’d use them.' }],
    keywords: ['subscription', 'billing', 'limits', 'add-ons'],
  },
  {
    id: 'profile-settings',
    title: 'Your profile and time zone',
    category: 'settings',
    summary: 'Your name, and the time zone school hours use.',
    where: '**Settings → Your profile**',
    body: [{ p: 'Your name is what your children see when you watch or talk. School hours and dates follow the time zone you pick here.' }],
    shots: [shot('settings-profile', 'Your profile')],
    keywords: ['name', 'timezone'],
  },
  {
    id: 'account-security',
    title: 'Account security',
    category: 'settings',
    summary: 'Password, passkeys and two-factor sign-in.',
    where: '**Settings → Account security**',
    body: [
      { list: [
        '**Change password:** signs out your other devices and emails you.',
        '**Passkeys:** sign in with your face, fingerprint or screen lock instead of a password.',
        '**Two-factor authentication:** a code from an authenticator app at sign-in, with recovery codes in case you lose your phone. Strongly recommended.',
      ] },
    ],
    shots: [shot('settings-security', 'Account security')],
    keywords: ['password', 'passkey', '2fa', 'two-factor', 'mfa', 'authenticator', 'sign in'],
    related: ['export-data', 'delete-account'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    category: 'settings',
    summary: 'Choose which alerts reach you by email or on this device.',
    where: '**Settings → Notifications**',
    body: [
      { p: 'For each kind of alert, tick **Email** and/or **On this device**: focus alerts, the app going offline during school time, “done for today”, help requests, chat messages, the whiteboard opening, and replies to your feedback.' },
      { p: '**Allow on-device notifications** asks your browser for permission; **Send test notification** checks it works.' },
    ],
    shots: [shot('settings-notifications', 'Notifications')],
    keywords: ['alerts', 'email', 'push', 'notify'],
    related: ['install-app'],
  },
  {
    id: 'appearance',
    title: 'Appearance',
    category: 'settings',
    summary: 'Light, dark, color themes and gradients.',
    where: '**Settings → Appearance**',
    body: [{ p: 'Pick how ClassGuardian looks. It follows your account to every device. Each child picks their own theme in the Student Portal.' }],
    shots: [shot('settings-appearance', 'Appearance')],
    keywords: ['theme', 'dark mode', 'colors', 'aurora'],
  },
  {
    id: 'api',
    title: 'API keys and webhooks',
    category: 'settings',
    summary: 'Let another system read your data or get events.',
    where: '**Settings → API & integrations**',
    body: [{ p: 'Create an API key (choose what it can read and when it expires) to let another system read your children’s data, or add a webhook URL to get events like alerts pushed to it. Both only ever cover the children you can see, and only what your role allows.' }],
    shots: [shot('settings-api', 'API & integrations')],
    keywords: ['integration', 'webhook', 'developer', 'automation'],
  },
  {
    id: 'export-data',
    title: 'Export my data',
    category: 'settings',
    summary: 'Download everything ClassGuardian holds about you and your children.',
    where: '**Settings → Account security → Export my data**',
    body: [{ p: '**Download my data** gives you one file with your account, sign-ins, families, and each child’s schedule, rules, devices, activity, planner, lesson plans, grades, attendance, reading and journal, plus your feedback requests. Passwords and keys are never included. You only get what your role lets you see.' }],
    keywords: ['download', 'gdpr', 'my data', 'backup'],
    related: ['delete-account', 'privacy'],
  },
  {
    id: 'delete-account',
    title: 'Delete my account',
    category: 'settings',
    summary: 'Erase your account and its data, with time to change your mind.',
    where: '**Settings → Account security → Delete my account**',
    body: [
      { p: 'Confirm with your password (and your authenticator code if you use two-factor). You’re signed out and emailed the date: after a grace period (14 days unless your server says otherwise) everything is erased. **Signing in before then keeps your account.**' },
      { p: 'A family you own goes with your account, unless other adults are in it, in which case make one of them the owner first. Children you added to someone else’s family stay with that family.' },
    ],
    keywords: ['delete', 'remove account', 'gdpr', 'erase', 'close account'],
    related: ['export-data', 'make-owner'],
  },
  {
    id: 'privacy',
    title: 'Privacy',
    category: 'settings',
    summary: 'What’s recorded, what isn’t, and for how long.',
    where: '**Settings → Privacy**',
    body: [
      { list: [
        'You can only watch during school time, never during breaks, and your child sees when you’re watching or talking, unless you turn on Hidden mode. With Hidden mode on, tell your child their computer is supervised.',
        'Live view isn’t recorded: no screen or camera recordings.',
        'Website history is kept for the server’s retention period (90 days by default), then deleted.',
        'Your records (grades, attendance, planner…) stay until you delete them or your account.',
      ] },
    ],
    shots: [shot('settings-privacy', 'Privacy')],
    keywords: ['recording', 'retention', 'safe', 'data'],
    related: ['export-data', 'delete-account'],
  },

  // --- Your child's side ----------------------------------------------------------------------------------------------
  {
    id: 'school-resources',
    title: 'School Resources on your child’s computer',
    category: 'student',
    summary: 'The panel your child uses during school time.',
    body: [
      { p: 'During school time a **School Resources** button sits in the corner of your child’s screen. It opens their planner (to tick off and turn in work), today’s schedule, grades, reading log and chat, with buttons to **ask for help**, **take a break** or say **I’m done for today**. Features you’ve hidden for them aren’t there.' },
    ],
    shots: [{ src: '/tour/desktop.jpg', caption: 'School Resources on a child’s desktop' }],
    keywords: ['agent', 'panel', 'child computer', 'windows app', 'mac app'],
    related: ['set-up-computer', 'student-portal', 'shared-computers'],
  },
  {
    id: 'student-portal',
    title: 'The Student Portal',
    category: 'student',
    summary: 'Your child’s own sign-in on the web.',
    where: 'Turn it on in your child’s **Profile**; they sign in at /student',
    body: [
      { p: 'Your child signs in with the code and password from their Profile. They see their **planner** (and turn work in with a note and a sticker), **grades**, **reading log** (adding what they read), **chat** with you, the **whiteboard**, how to **set up devices**, and their own requests to support. They can ask for help or a break, and pick their own theme. They only ever see their own information.' },
    ],
    shots: [shot('student-planner', 'The Student Portal: planner'), shot('student-grades', 'Their grades'), shot('student-reading', 'Adding today’s reading'), shot('student-chat', 'Chat with you'), shot('student-setup', 'Setting up a device')],
    keywords: ['student login', 'portal', 'kid login', 'turn in', 'stickers'],
    related: ['child-profile', 'reading-log', 'planner'],
  },
  {
    id: 'shared-computers',
    title: 'Shared computers',
    category: 'student',
    summary: 'One computer, several children: each signs in as themselves.',
    where: 'Devices → mark a computer **shared**',
    body: [{ p: 'On a shared computer, School Resources asks who’s using it and has a **Not you? Switch student** button, so each child gets their own planner, schedule and rules without typing a password.' }],
    keywords: ['family computer', 'switch student', 'siblings'],
    related: ['devices', 'school-resources'],
  },
  {
    id: 'browser-extension',
    title: 'The browser extension',
    category: 'student',
    summary: 'Blocks websites and logs visits during school time.',
    body: [{ p: 'The ClassGuardian extension (Chrome or Edge) enforces Safety Controls in the browser and records the website log in Online Activity. Pair it with a Family code like the app. On a Chromebook it’s the main way ClassGuardian runs.' }],
    keywords: ['extension', 'chrome', 'edge', 'chromebook', 'website blocking'],
    related: ['safety-controls', 'set-up-computer'],
  },

  // --- Help & support --------------------------------------------------------------------------------------------------
  {
    id: 'getting-help',
    title: 'Getting help',
    category: 'help',
    summary: 'Call, text or email us, or send feedback.',
    where: 'Account menu → **Help**, **Feedback** or **Contact us**',
    body: [
      { list: [
        '**Contact us** for anything urgent: call, text or email ClassGuardian support.',
        '**Feedback** for questions, problems and ideas. Follow the replies there; a dot on Settings and your avatar shows a new reply.',
        '**Help** has the setup steps and this guide.',
      ] },
    ],
    shots: [shot('help', 'Help'), shot('feedback', 'Feedback')],
    keywords: ['support', 'contact', 'phone', 'email', 'feedback', 'bug', 'question'],
  },
];
